import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {BookingCalendarStates, Stats, VehicleEntryStatus, VehiclesModel} from './booking-calendar.model';
import {FormControl, Validators} from '@angular/forms';
import {CalendarHeaderComponent} from '@shared/components/calendar-header/calendar-header.component';
import {DateUtilities} from '@shared/utilities/date-utilities.model';
import {Subscription} from 'rxjs';
import {TranslateContext} from '@shared/pipes/translate.pipe';
import {Module, ModuleService} from '@shared/service/module.service';

@Component({
	selector: 'amd-booking-calendar',
	templateUrl: './booking-calendar.component.html',
	styleUrls: ['./booking-calendar.component.scss']
})
export class BookingCalendarComponent implements OnInit, OnDestroy, OnChanges {

	/** the input array of vehicles to view in the calendar {@link VehiclesModel} */
	@Input() vehicles: VehiclesModel[];

	/** input parameter for enable the edit of element default value --> false*/
	@Input() canEdit: boolean = false;

	/** input parameter for pagination details {@link Stats}*/
	@Input() pageStats: Stats;

	/** input parameter for the date reset*/
	@Input() reset: any;

	/** output event for the date change, it emit {@link start_date and @link end_date} */
	@Output() onDateChange: EventEmitter<{ start_date: string, end_date: string }> = new EventEmitter<{ start_date: string, end_date: string }>();

	/** output event for open and edit dialog for single element, it emit the vehicle and the element date
	 * that you have chosen */
	@Output() openEditDialog?: EventEmitter<{ vehicle: VehiclesModel, element_date: string }>;

	/** output event for pagination changes*/
	@Output() paginationChanges: EventEmitter<Stats> = new EventEmitter<Stats>();

	calendarHeader: typeof CalendarHeaderComponent = CalendarHeaderComponent;
	dateControl: FormControl;
	start_date: string;
	end_date: string;
	dateSubscription: Subscription;
	showCalendar: boolean = false;
	renderedElements: { day: number, month: number, date: string }[] = [];
	calendarStates: string[] = <string[]>Object.keys(BookingCalendarStates);
	bookingCalendarStates: typeof BookingCalendarStates = BookingCalendarStates;
	calendarTranslateContext: TranslateContext = TranslateContext.BOOKING_CALENDAR;
	translateContext: typeof TranslateContext = TranslateContext;

	is_bookings: boolean = false;

	constructor(
		private moduleService: ModuleService
	) {
		this.is_bookings = this.moduleService.getCurrentModule() === Module.BOOKINGS;
		this.dateControl = new FormControl();
		this.dateControl.setValidators(Validators.required);
		this.dateControl.setValue(DateUtilities.getStringDateFromDate(new Date()));
		this.getEndDate();
	}

	ngOnInit(): void {
		this.getEndDate();

		this.dateSubscription = this.dateControl.valueChanges.subscribe(() => {
			this.getEndDate();
		});

		if (this.canEdit) {
			this.openEditDialog = new EventEmitter<{ vehicle: VehiclesModel, element_date: string }>();
		}
	}

	ngOnDestroy(): void {
		if (this.dateSubscription) {
			this.dateSubscription.unsubscribe();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && (changes.vehicles && (changes.vehicles.previousValue !== changes.vehicles.currentValue))) {
			this.vehicles = <VehiclesModel[]>changes.vehicles.currentValue;
		}

		if (changes && (changes.pageStats && (changes.pageStats.previousValue !== changes.pageStats.currentValue))) {
			this.pageStats = <Stats>changes.pageStats.currentValue;
		}
		if (changes && changes.reset) {
			this.resetToCurrentDate();
		}
	}

	/** calculate the {@link end_date} for the given {@link start_date}*/
	getEndDate(): void {
		this.showCalendar = false;

		const start: string = DateUtilities.getStringDateFromDate(this.dateControl.value);
		this.start_date = `${Number.parseInt(start.split('-')[0])}-${Number.parseInt(start.split('-')[1])}-${Number.parseInt(start.split('-')[2])}`;

		this.end_date = DateUtilities.getEndDateIntervalForCalendarFromStartDate(this.start_date);

		this.getRenderedElements();
		this.showCalendar = true;
		this.onDateChange.emit({start_date: this.start_date, end_date: this.end_date});
	}

	/** reset the {@link start_date} to current date*/
	resetToCurrentDate(): void {
		this.dateControl.setValue(DateUtilities.getStringDateFromDate(new Date()));
	}

	/** prepare the {@link renderedElements} for rendering the calendar from {@link start_date} to {@link end_date}*/
	getRenderedElements(): void {
		this.renderedElements = [];
		let day: number = Number.parseInt(this.start_date.split('-')[2]);
		let month: number = Number.parseInt(this.start_date.split('-')[1]);
		let year: number = Number.parseInt(this.start_date.split('-')[0]);

		this.renderedElements.push({day: day, month: month, date: `${year}-${month}-${day}`});
		while (`${year}-${month}-${day}` !== this.end_date) {
			const return_date: { day: number, month: number, year: number } = this.getForwardDate(day, month, year);
			day = return_date.day;
			month = return_date.month;
			year = return_date.year;
			this.renderedElements.push({day: day, month: month, date: `${year}-${month}-${day}`});
		}

	}

	/** return the css class for the element if the  element_date is between
	 * {@link VehicleEntryStatus#start_at} and {@link VehicleEntryStatus#end_at}*/
	getElementClass(vehicle_entry: VehicleEntryStatus[], element_date: string, status_block: string): string {
		let element_class: string = '';
		if (!!vehicle_entry && vehicle_entry.length) {
			vehicle_entry.forEach(
				entry => {
					if (!!entry && !!entry.start_at && !!entry.end_at) {
						const
							entry_start_date: string = DateUtilities.getStringDateFromDate(new Date(entry.start_at)),
							entry_end_date: string = DateUtilities.getStringDateFromDate(new Date(entry.end_at)),
							start_date: number =
								Date.parse(`${Number.parseInt(entry_start_date.split('-')[0])}-${Number.parseInt(entry_start_date.split('-')[1])}-${Number.parseInt(entry_start_date.split('-')[2])}`),
							end_date: number =
								Date.parse(`${Number.parseInt(entry_end_date.split('-')[0])}-${Number.parseInt(entry_end_date.split('-')[1])}-${Number.parseInt(entry_end_date.split('-')[2])}`),
							date_to_check: number = Date.parse(element_date);

						if (start_date <= date_to_check && end_date >= date_to_check) {
							element_class = entry.status;
						}

						if (!!entry.contingency) {
							let
								start: { day: number, month: number, year: number } = {
									day: Number.parseInt(entry_start_date.split('-')[2]),
									month: Number.parseInt(entry_start_date.split('-')[1]),
									year: Number.parseInt(entry_start_date.split('-')[0])
								},
								end: { day: number, month: number, year: number } = {
									day: Number.parseInt(entry_end_date.split('-')[2]),
									month: Number.parseInt(entry_end_date.split('-')[1]),
									year: Number.parseInt(entry_end_date.split('-')[0])
								};

							for (let i: number = 0; i < entry.contingency; i++) {
								start = this.getPreviousDate(start.day, start.month, start.year);
								end = this.getForwardDate(end.day, end.month, end.year);
							}
							const
								start_date_with_contingency: number = Date.parse(`${start.year}-${start.month}-${start.day}`),
								end_date_with_contingency: number = Date.parse(`${end.year}-${end.month}-${end.day}`);

							if (
								(start_date_with_contingency <= date_to_check && start_date > date_to_check) ||
								(end_date < date_to_check && end_date_with_contingency >= date_to_check)
							) {
								element_class = BookingCalendarStates.CONTINGENCY;
							}
						}
					}
				}
			);
		} else {
			element_class = status_block;
		}

		return 'rendered-element--' + element_class;
	}

	/** trigger the movement of the calendar day by day*/
	moveDate(is_forward: boolean): void {
		const start_date: string = DateUtilities.getStringDateFromDate(this.dateControl.value);
		const day: number = Number.parseInt(start_date.split('-')[2]);
		const month: number = Number.parseInt(start_date.split('-')[1]);
		const year: number = Number.parseInt(start_date.split('-')[0]);

		const return_date: { day: number, month: number, year: number } =
			is_forward ?
				this.getForwardDate(day, month, year) :
				this.getPreviousDate(day, month, year);

		this.dateControl.setValue(DateUtilities.getStringDateFromDate(DateUtilities.getDate(`${return_date.year}-${return_date.month}-${return_date.day}`)));
	}

	/** return an object that contains day month and year of the day after the date you give in input */
	getForwardDate(day: number, month: number, year: number): { day: number, month: number, year: number } {
		switch (true) {
			case day === 28 && month === 2:
				const temp_date: string = DateUtilities.getStringDateFromDate(new Date(`${year}-${month}-${day + 1}`));
				return {
					day: Number.parseInt(temp_date.split('-')[2]),
					month: Number.parseInt(temp_date.split('-')[1]),
					year: Number.parseInt(temp_date.split('-')[0])
				};
			case day === 29 && month === 2:
			case day === 30 && month === 4:
			case day === 30 && month === 6:
			case day === 30 && month === 9:
			case day === 30 && month === 11:
			case day === 31 && month === 1:
			case day === 31 && month === 3:
			case day === 31 && month === 5:
			case day === 31 && month === 7:
			case day === 31 && month === 8:
			case day === 31 && month === 10:
				month++;
				day = 1;
				return {day: day, month: month, year: year};
			case day === 31 && month === 12:
				month = 1;
				day = 1;
				year++;
				return {day: day, month: month, year: year};
			default:
				day++;
				return {day: day, month: month, year: year};
		}
	}

	/** return an object that contains day month and year of the day before the date you give in input */
	getPreviousDate(day: number, month: number, year: number): { day: number, month: number, year: number } {
		switch (true) {
			case day === 1 && month === 1:
				year--;
				return {year: year, month: 12, day: 31};
			case day === 1 && month === 2:
			case day === 1 && month === 4:
			case day === 1 && month === 6:
			case day === 1 && month === 9:
			case day === 1 && month === 11:
				month--;
				return {year: year, month: month, day: 31};
			case day === 1 && month === 3:
				month--;
				return {year: year, month: month, day: 28};
			case day === 1 && month === 5:
			case day === 1 && month === 7:
			case day === 1 && month === 8:
			case day === 1 && month === 10:
			case day === 1 && month === 12:
				month--;
				return {year: year, month: month, day: 30};
			default:
				day--;
				return {year: year, month: month, day: day};
		}
	}

	/** it emit an event with the single vehicle {@link VehiclesModel} and the element_date */
	emitEditEvent(vehicle: VehiclesModel, element_date: string): void {
		if (this.canEdit) {
			this.openEditDialog.emit({vehicle: vehicle, element_date: element_date});
		}
	}

	onNextClick(): void {
		this.pageStats.current_page++;
		this.paginationChanges.emit(this.pageStats);
	}

	onPreviousClick(): void {
		this.pageStats.current_page--;
		this.paginationChanges.emit(this.pageStats);
	}

	setItemPerPage($event: number): void {
		this.pageStats.page_size = $event;
		this.paginationChanges.emit(this.pageStats);
	}
}
